import { useToast } from '@chakra-ui/react'
import { upperFirst } from 'lodash'
import { useState } from 'react'

import { getTokens } from './auth'

export enum DownloadType {
  Refresh = 'Refresh',
  DownloadNew = 'Download',
}
export enum RequestType {
  CompetitorsRequest = 'CompetitorsRequest',
  TournamentsRequest = 'TournamentsRequest',
}
export type StatusType = 'success' | 'error'

export const useCompetitionSync = () => {
  const [sending, setSending] = useState(false)
  const toast = useToast()

  const sendFeedToast = (type: RequestType, status: StatusType) => {
    toast({
      title: `${upperFirst(type)} feed ${status === 'success' ? 'sent successfully' : 'send failed'}`,
      status: status,
      duration: 3000,
    })
  }

  const sendRefreshDLRequest = async (type: RequestType, compId: string, clearCache: boolean = false) => {
    setSending(true)
    const tokens = getTokens()
    let baseUrl = `${import.meta.env.VITE_API_URL}sds/comp/refresh/${compId}`
    if (type === RequestType.TournamentsRequest) {
      baseUrl = `${import.meta.env.VITE_API_URL}sds/comp/download/${compId}`
    }

    const url = new URL(baseUrl)
    if (clearCache) {
      url.searchParams.append('clearCache', 'true')
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` },
      })
      if (response) {
        setSending(false)
        sendFeedToast(type, response.ok ? 'success' : 'error')
      }
    } catch (error) {
      console.warn('Error sending Feed', error) // eslint-disable-line no-console
      setSending(false)
      sendFeedToast(type, 'error')
    }
  }

  // Function to call the rename-names API
  const sendRenameNamesRequest = async (compId: string) => {
    const tokens = getTokens()
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}admin/competitions/${compId}/rename-names`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        toast({
          title: 'Competition and matches renamed',
          description: data.message || 'Operation successful.',
          status: 'success',
          duration: 3000,
        })
      } else {
        toast({
          title: 'Rename failed',
          description: 'Could not rename competition and matches.',
          status: 'error',
          duration: 3000,
        })
      }
    } catch (error) {
      toast({
        title: 'Rename failed',
        description: 'An error occurred.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // Function to call the revert-names API
  const sendRevertNamesRequest = async (compId: string) => {
    const tokens = getTokens()
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}admin/competitions/${compId}/revert-names`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        toast({
          title: 'Competition and matches reverted',
          description: data.message || 'Names reverted to backup.',
          status: 'success',
          duration: 3000,
        })
      } else {
        toast({
          title: 'Revert failed',
          description: 'Could not revert competition and matches.',
          status: 'error',
          duration: 3000,
        })
      }
    } catch (error) {
      toast({
        title: 'Revert failed',
        description: 'An error occurred.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  return {
    sending,
    sendRefreshDLRequest,
    sendRenameNamesRequest,
    sendRevertNamesRequest,
  }
}
